import { useState } from 'react'
import { fbt } from 'fbtee'
import { Check, CircleDashed, Clock, X } from 'lucide-react'
import { TICKET_NAME_MAX, type TicketSlotInput } from '@shared/eventTickets'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { UserAvatar } from '../../../components/UserAvatar'
import { OffAppAvatar } from './TicketMarks'
import { TicketHolderPicker } from './TicketHolderPicker'
import { friendFallbackName } from '../../../lib/commonStrings'
import type { UserProfile } from '../../../types'

export type DraftSlot = TicketSlotInput & { accepted?: boolean }

const valueOf = (s: DraftSlot) => (s.kind === 'user' ? `user:${s.userId}` : s.kind)

/** One ticket in the owner's editor: who it's for, and a way to drop it. */
export function TicketSlotRow({ index, slot, selfProfile, people, profiles, seekerIds, goingUserIds, interestedUserIds, taken, meTaken, onChange, onRemove }: {
  index: number
  slot: DraftSlot
  /** The viewer: a 'Me' ticket shows their own avatar. */
  selfProfile?: UserProfile
  /** Crew members to offer, seekers first; plus whoever this slot already names. */
  people: string[]
  profiles: Record<string, UserProfile>
  seekerIds: ReadonlySet<string>
  goingUserIds: ReadonlySet<string>
  interestedUserIds: ReadonlySet<string>
  /** People another row already names. */
  taken: ReadonlySet<string>
  meTaken: boolean
  onChange: (slot: DraftSlot) => void
  onRemove: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  const nameOf = (uid: string) => profiles[uid]?.displayName?.trim() || friendFallbackName()

  function pick(value: string) {
    if (value === 'me' || value === 'open') onChange({ id: slot.id, kind: value })
    else if (value === 'name') onChange({ id: slot.id, kind: 'name', name: '' })
    else onChange({ id: slot.id, kind: 'user', userId: value.slice('user:'.length) })
  }

  return (
    <li className="rounded-lg border border-border bg-card" data-testid={`ticket-slot-${index}`}>
      <div className="flex h-10 items-center gap-1 pl-2.5 pr-1">
        <SlotIcon slot={slot} profile={slot.kind === 'me' ? selfProfile : slot.userId ? profiles[slot.userId] : undefined} />
        <TicketHolderPicker
          value={valueOf(slot)}
          label={slot.kind === 'me'
            ? fbt('Me', 'Ticket picker option: the ticket is for the viewer')
            : slot.kind === 'open'
              ? fbt('Open', 'Ticket picker option: the ticket has no owner yet, so the crew can claim it')
              : slot.kind === 'name'
                ? fbt('Someone not on Faro…', 'Ticket picker option: type the name of someone without a Faro account')
                : nameOf(slot.userId!)}
          people={people}
          profiles={profiles}
          selfProfile={selfProfile}
          seekerIds={seekerIds}
          goingUserIds={goingUserIds}
          interestedUserIds={interestedUserIds}
          taken={taken}
          meTaken={meTaken}
          onPick={pick}
        />
        {slot.kind === 'user' && (slot.accepted ? (
          <Check className="size-4 shrink-0 text-going" aria-label={fbt('Said yes', 'Hint on a ticket the person already accepted')} />
        ) : (
          <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            <Clock className="size-3" />
            {fbt('Pending', 'Status on a ticket in the editor whose person hasn’t answered yet')}
          </span>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={fbt('Remove ticket', 'Accessible label of the button removing one ticket in the tickets editor')}
          onClick={() => (slot.kind === 'user' && slot.accepted ? setConfirming(true) : onRemove())}
        >
          <X />
        </Button>
      </div>
      {slot.kind === 'name' && (
        <div className="px-2.5 pb-2.5">
          <Input
            value={slot.name ?? ''}
            maxLength={TICKET_NAME_MAX}
            autoFocus={!slot.name}
            placeholder={fbt('Their name', 'Placeholder of the name field for a ticket holder without a Faro account')}
            onChange={e => onChange({ ...slot, name: e.target.value })}
          />
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={fbt(fbt.param('name', slot.userId ? nameOf(slot.userId) : '') + ' already said yes — take them off?', 'Title of the confirm before removing a ticket someone already accepted')}
        description={fbt('They’ll get a notification.', 'Body of the confirm before removing a ticket someone already accepted')}
        confirmLabel={fbt('Take them off', 'Confirm button removing a ticket someone already accepted')}
        onConfirm={onRemove}
      />
    </li>
  )
}

function SlotIcon({ slot, profile }: { slot: DraftSlot; profile?: UserProfile }) {
  if (slot.kind === 'user' || slot.kind === 'me') return <UserAvatar profile={profile} fallbackName={profile?.displayName} size="xs" />
  if (slot.kind === 'name') return <OffAppAvatar />
  return (
    <span className="flex size-6 shrink-0 items-center justify-center">
      <CircleDashed className="size-4 text-muted-foreground" />
    </span>
  )
}
